import styled from '@emotion/styled'
import {
  OPT_TASKS_LIST_FREQUENCY_DAILY,
  OPT_TASKS_LIST_FREQUENCY_MONTHLY,
  OPT_TASKS_LIST_FREQUENCY_WEEKLY,
  OPT_TASKS_LIST_TYPE_EMAIL_ID,
  OPT_TASKS_LIST_TYPE_SMS_ID
} from 'Constants'
import { formatStringDatetime } from 'Helpers/dates'
import {
  Accordion,
  ACCORDION_VARIANT_LIGHT,
  DataListExtraColumn,
  DataListRow,
  Divider,
  Flex,
  Grid,
  GridCell,
  IconEmail,
  IconMessage1,
  IconUser,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  SPACING_16,
  SPACING_4,
  SpacingVertical,
  Text,
  TEXT_VARIANT_BOLD,
  TEXT_VARIANT_SMALL_LIGHT,
  Toggle,
  TooltipUtils,
  TruncateAndTooltip
} from 'mailup-web-components'
import {
  fetchMoreResultsCallback,
  setStatusTaskCallback
} from 'Pages/TasksList/recoil/callbacks/callbacks'
import { moreResults } from 'Pages/TasksList/recoil/state/search'
import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import { useRecoilCallback, useRecoilValue } from 'recoil'
import ActionsMenu from './ActionsMenu'

const StyledFrequency = styled.span`
  text-transform: capitalize;
`
const StyledDataListExtraColumn = styled(DataListExtraColumn)`
  min-width: 80px;
`

const Row = ({ item, heads }) => {
  const intl = useIntl()
  const history = useHistory()

  const fetchMoreResults = useRecoilCallback(fetchMoreResultsCallback, [])
  const setStatusTask = useRecoilCallback(setStatusTaskCallback, [])
  const { moreMessageDetails, moreCountMessage } = useRecoilValue(
    moreResults(item.Id)
  )

  const { statusToggle } = useRecoilValue(moreResults(item.Id))

  useEffect(() => {
    fetchMoreResults(item.Id, item.Detail.IdMessage, item.Detail.Action)
    setStatusTask(item.Id, item.Enabled, true)
  }, [item.Id])

  useEffect(() => {
    TooltipUtils.rebuild()
  }, [])

  const handleToggleTestStatus = (e) => {
    setStatusTask(item.Id, e.target.checked)
  }

  const handleEdit = () => {
    history.push(`/automation/task/${item.Id}`)
  }

  const COLUMN_BEFORE_WIDTH = '15px'
  const COLUMN_AFTER_WIDTH = '80px'

  const renderIcon = (actionType) => {
    switch (actionType) {
      case OPT_TASKS_LIST_TYPE_EMAIL_ID:
        return (
          <IconEmail
            data-tip={intl.formatMessage({
              id: 'Load.TriggersList.TooltipIconEmail'
            })}
          />
        )
      case OPT_TASKS_LIST_TYPE_SMS_ID:
        return (
          <IconMessage1
            data-tip={intl.formatMessage({
              id: 'Load.TriggersList.TooltipIconSMS'
            })}
          />
        )
      default:
        return (
          <IconUser
            data-tip={intl.formatMessage({
              id: 'Load.TriggersList.TooltipIconGroups'
            })}
          />
        )
    }
  }

  const renderActionDescription = (actionType, idMessage, subject) => {
    switch (actionType) {
      case OPT_TASKS_LIST_TYPE_EMAIL_ID:
        return `${intl.formatMessage({
          id: 'Load.TriggersList.ActionSendEmail'
        })} ${idMessage} - ${
          subject || intl.formatMessage({ id: 'Load.TaskList.MsgDeleted' })
        }`
      case OPT_TASKS_LIST_TYPE_SMS_ID:
        return `${intl.formatMessage({
          id: 'Load.TriggersList.ActionSendSMS'
        })} ${idMessage} - ${
          subject || intl.formatMessage({ id: 'Load.TaskList.MsgDeleted' })
        }`
      default:
        return intl.formatMessage({
          id: 'Load.TriggersList.CopyMoveInAGroup'
        })
    }
  }

  const renderFrequency = (frequency) => {
    switch (frequency) {
      case OPT_TASKS_LIST_FREQUENCY_DAILY:
        return intl.formatMessage({
          id: 'Load.TriggersList.Frequency-d'
        })
      case OPT_TASKS_LIST_FREQUENCY_WEEKLY:
        return intl.formatMessage({
          id: 'Load.TriggersList.Frequency-w'
        })
      case OPT_TASKS_LIST_FREQUENCY_MONTHLY:
        return intl.formatMessage({
          id: 'Load.TriggersList.Frequency-m'
        })
      default:
        return intl.formatMessage({
          id: 'Load.TriggersList.Frequency-y'
        })
    }
  }

  return (
    <DataListRow data-mu-test='tasks-list-item'>
      <DataListExtraColumn before width={COLUMN_BEFORE_WIDTH}>
        {renderIcon(item.Detail.Action)}
      </DataListExtraColumn>
      <Flex flexDirection='column'>
        <Grid cols={12}>
          <GridCell span={heads[0].span}>
            <Link
              onClick={handleEdit}
              variant={LINK_VARIANT_HIGHLIGHT}
              data-mu-test={`tasks-list-Name-${item.Id}`}
            >
              <TruncateAndTooltip text={item.Name} />
            </Link>
            <SpacingVertical variant={SPACING_4} />

            <Text variant={TEXT_VARIANT_SMALL_LIGHT}>
              {intl.formatMessage({
                id: 'Load.MailupCommon.Id'
              })}{' '}
              {`${item.Id} - `}
              {renderActionDescription(
                item.Detail.Action,
                item.Detail.IdMessage,
                moreMessageDetails ? moreMessageDetails.Subject : null
              )}
            </Text>
            <SpacingVertical variant={SPACING_4} />
          </GridCell>
          <GridCell
            span={heads[1].span}
            data-mu-test={`tasks-list-NextRealRunDate-${item.Id}`}
          >
            {formatStringDatetime(item.NextRealRunDate)}
          </GridCell>
          <GridCell span={heads[2].span}>
            <StyledFrequency>
              {renderFrequency(item.Frequency.KindDateFrequency)}
            </StyledFrequency>
          </GridCell>
          <GridCell span={heads[3].span}>
            <Toggle
              checked={statusToggle}
              onChange={handleToggleTestStatus}
              name={`statusToggle-${item.Id}`}
              data-mu-test='triggers-toggle-status'
              value='true'
            />
          </GridCell>
        </Grid>
        <Grid cols={12}>
          <GridCell span={12}>
            <Accordion
              label={intl.formatMessage({
                id: 'Load.MailupCommon.MoreDetails'
              })}
              variant={ACCORDION_VARIANT_LIGHT}
            >
              <SpacingVertical variant={SPACING_16} />
              <Divider />
              <SpacingVertical variant={SPACING_16} />

              <Grid cols={12}>
                <GridCell span={heads[0].span}>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.TriggersList.Description'
                    })}
                  </Text>
                  <SpacingVertical variant={SPACING_4} />
                </GridCell>
                <GridCell span={heads[1].span}>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.TriggersList.ActiveFrom'
                    })}
                  </Text>
                  <SpacingVertical variant={SPACING_4} />
                </GridCell>
                <GridCell span={heads[2].span}>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.TriggersList.ActiveTO'
                    })}
                  </Text>
                  <SpacingVertical variant={SPACING_4} />
                </GridCell>
                <GridCell span={heads[3].span}>
                  <Text variant={TEXT_VARIANT_BOLD}>
                    {intl.formatMessage({
                      id: 'Load.TriggersList.Time'
                    })}
                  </Text>
                  <SpacingVertical variant={SPACING_4} />
                </GridCell>
              </Grid>
              <Grid cols={12}>
                <GridCell span={heads[0].span}>
                  {item.Description ? item.Description : '-'}
                </GridCell>
                <GridCell span={heads[1].span}>
                  {formatStringDatetime(item.StartDate, 'date')}
                </GridCell>
                <GridCell span={heads[2].span}>
                  {item.EndDate
                    ? formatStringDatetime(item.EndDate, 'date')
                    : '-'}
                </GridCell>
                <GridCell span={heads[3].span}>
                  {formatStringDatetime(item.NextRealRunDate, 'time')}
                </GridCell>
              </Grid>
            </Accordion>
          </GridCell>
        </Grid>
      </Flex>

      <StyledDataListExtraColumn after width={COLUMN_AFTER_WIDTH}>
        <ActionsMenu item={item} moreCountMessage={moreCountMessage} />
      </StyledDataListExtraColumn>
    </DataListRow>
  )
}

export default Row

Row.propTypes = {
  item: PropTypes.object,
  heads: PropTypes.array
}
